import { hasText, stringOrEmpty } from '@utils/helpers/lib/utils/utils';

type BadgeColorVariant = 'filled' | 'text';

type PlanBadgeColorOptions = {
  colorByPlan?: Record<string, string>;
  variant?: BadgeColorVariant;
};

const DEFAULT_FILLED_BADGE_COLOR = 'bg-gray-100 text-gray-800';
const DEFAULT_TEXT_BADGE_COLOR = 'text-gray-800';

const dynamicFilledPlanBadgeColors = [
  'bg-blue-100 text-blue-800',
  'bg-emerald-100 text-emerald-800',
  'bg-amber-100 text-amber-800',
  'bg-fuchsia-100 text-fuchsia-800',
  'bg-cyan-100 text-cyan-800',
  'bg-slate-100 text-slate-800'
];

const dynamicTextPlanBadgeColors = [
  'text-blue-800',
  'text-emerald-800',
  'text-amber-800',
  'text-fuchsia-800',
  'text-cyan-800',
  'text-slate-800'
];

const fixedPlanBadgeColors: Record<
  string,
  Record<BadgeColorVariant, string>
> = {
  premium: {
    filled: 'bg-yellow-100 text-yellow-800',
    text: 'text-yellow-800'
  },
  standard: {
    filled: 'bg-green-100 text-green-800',
    text: 'text-green-800'
  },
  basic: {
    filled: DEFAULT_FILLED_BADGE_COLOR,
    text: DEFAULT_TEXT_BADGE_COLOR
  },
  trial: {
    filled: 'bg-blue-100 text-blue-800',
    text: 'text-blue-800'
  },
  'family plus': {
    filled: 'bg-purple-100 text-purple-800',
    text: 'text-purple-800'
  },
  family: {
    filled: 'bg-blue-100 text-blue-800',
    text: 'text-blue-800'
  },
  individual: {
    filled: DEFAULT_FILLED_BADGE_COLOR,
    text: DEFAULT_TEXT_BADGE_COLOR
  },
  subscribed: {
    filled: 'bg-green-100 text-green-800',
    text: 'text-green-800'
  }
};

const dynamicRoleBadgeColors = [
  'bg-purple-100 text-purple-800',
  'bg-blue-100 text-blue-800',
  'bg-green-100 text-green-800',
  'bg-orange-100 text-orange-800',
  'bg-pink-100 text-pink-800',
  'bg-indigo-100 text-indigo-800'
];

export const normalizePlanKey = (value: string) =>
  stringOrEmpty(value).trim().toLowerCase();

const getStringHash = (value: string) => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
};

const getDynamicPlanBadgeColor = (
  normalizedPlan: string,
  variant: BadgeColorVariant
) => {
  const palette =
    variant === 'text'
      ? dynamicTextPlanBadgeColors
      : dynamicFilledPlanBadgeColors;

  return palette[getStringHash(normalizedPlan) % palette.length];
};

export const getPlanBadgeColor = (
  plan: string,
  options: PlanBadgeColorOptions = {}
) => {
  const normalizedPlan = normalizePlanKey(plan);
  const variant = options.variant || 'filled';
  const defaultColor =
    variant === 'text' ? DEFAULT_TEXT_BADGE_COLOR : DEFAULT_FILLED_BADGE_COLOR;

  if (!hasText(normalizedPlan)) {
    return defaultColor;
  }

  const mappedColor = options.colorByPlan?.[normalizedPlan];

  if (hasText(mappedColor)) {
    return mappedColor;
  }

  const fixedColor = fixedPlanBadgeColors[normalizedPlan]?.[variant];

  if (hasText(fixedColor)) {
    return fixedColor;
  }

  return getDynamicPlanBadgeColor(normalizedPlan, variant);
};

export const getRoleBadgeColor = (role: string) => {
  const normalizedRole = normalizePlanKey(role);

  if (!hasText(normalizedRole)) {
    return DEFAULT_FILLED_BADGE_COLOR;
  }

  return dynamicRoleBadgeColors[
    getStringHash(normalizedRole) % dynamicRoleBadgeColors.length
  ];
};

export const getStatusBadgeColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
    case 'approved':
    case 'approve media':
    case 'active account':
    case 'completed':
    case 'paid':
    case 'success':
    case 'succeeded':
    case 'subscribed':
      return 'bg-green-100 text-green-800';
    case 'trialing':
      return 'bg-blue-100 text-blue-800';
    case 'suspended':
    case 'rejected':
    case 'failed':
    case 'churned':
      return 'bg-red-100 text-red-800';
    case 'pending':
    case 'under review':
      return 'bg-yellow-100 text-yellow-800';
    case 'inactive':
    case 'flagged':
    case 'need review':
    case 'needs review':
    case 'refunded':
    case 'unsubscribed':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

export const getLabelBadgeColor = (label: string) => {
  const normalized = (label || '').toLowerCase();
  if (
    normalized.includes('explicit') ||
    normalized.includes('violence') ||
    normalized.includes('nudity')
  ) {
    return 'bg-red-100 text-red-800';
  }
  if (
    normalized.includes('hate') ||
    normalized.includes('swimwear') ||
    normalized.includes('underwear') ||
    normalized.includes('rude')
  ) {
    return 'bg-orange-100 text-orange-800';
  }
  if (
    normalized.includes('spam') ||
    normalized.includes('disturbing') ||
    normalized.includes('alcohol') ||
    normalized.includes('tobacco')
  ) {
    return 'bg-yellow-100 text-yellow-800';
  }
  if (normalized.includes('safe')) {
    return 'bg-green-100 text-green-800';
  }
  return 'bg-gray-100 text-gray-800';
};

export const getConfidenceColor = (confidence: number) => {
  if (confidence >= 90) return 'bg-red-500';
  if (confidence >= 60) return 'bg-orange-500';
  return 'bg-green-500';
};
